import { useDashboardPageState } from '../../../hooks/useDashboardPageState';
import {
  TimeChartConfig,
  TimeChartLegendConfig,
} from '../../Viz/TimeChart/types';
import { Trend } from '../../../models/score';
import { useCallback, useMemo, useState } from 'react';
import { useToken } from '@chakra-ui/react';
import { DataRange, Metrics } from '../types';
import {
  computeNotMemberViewMetrics,
  generateTimeChartConfigIfMemberView,
  generateTimeChartConfigIfNotMemberView,
  generateTimeChartDataIfMemberView,
  generateTimeChartDataIfNotMemberView,
} from '../utils';

// Key-value pairs describing the visibility of the series
type VisibilityMap = { [key in Metrics]?: boolean };

const visibilityMapInitialState: VisibilityMap = {
  [Metrics.score]: true,
  [Metrics.stress]: true,
};

export const usePanel = () => {
  const [secondary200, secondary500, primary500, orange500] = useToken(
    'colors',
    ['secondary.200', 'secondary.500', 'primary.500', 'orange.500']
  );

  const {
    state: { all_scores_data, over_time_score, today_score, userDiagnosis },
  } = useDashboardPageState();

  const rawDataPoints = useMemo(
    () => all_scores_data.slice(),
    [all_scores_data]
  );

  const trend = over_time_score >= today_score ? Trend.down : Trend.up;

  const [isMemberViewActive, setIsMemberViewActive] = useState(true);
  const [dataRange, setDataRange] = useState<DataRange>(30);

  const [visibilityMap, setVisibilityMap] = useState<VisibilityMap>(
    visibilityMapInitialState
  );

  const onToggleLegendItem = useCallback(
    (dataKey: string, visible: boolean) => {
      if (!isMemberViewActive && !visible) {
        setVisibilityMap({ [dataKey]: true });
        return;
      }

      setVisibilityMap((map) => ({
        ...map,
        [dataKey]: !visible,
      }));
    },
    [isMemberViewActive]
  );

  const timeChartData = useMemo(() => {
    const rangedDataPoints =
      dataRange > 0 ? rawDataPoints.slice(0, dataRange) : rawDataPoints;
    const chronologicallySortedDataPoints = rangedDataPoints.reverse();

    if (isMemberViewActive) {
      return generateTimeChartDataIfMemberView(chronologicallySortedDataPoints);
    }

    return generateTimeChartDataIfNotMemberView(
      chronologicallySortedDataPoints
    );
  }, [dataRange, isMemberViewActive, rawDataPoints]);

  const timeChartConfig = useMemo<TimeChartConfig>(() => {
    const colors = { secondary200, secondary500, primary500, orange500 };

    if (isMemberViewActive) {
      return generateTimeChartConfigIfMemberView(colors, visibilityMap);
    }

    return generateTimeChartConfigIfNotMemberView(
      colors,
      visibilityMap,
      timeChartData,
      userDiagnosis
    );
  }, [
    secondary200,
    secondary500,
    primary500,
    orange500,
    isMemberViewActive,
    timeChartData,
    userDiagnosis,
    visibilityMap,
  ]);

  const legendConfig = useMemo<TimeChartLegendConfig>(() => {
    const actualVisibilityMap = isMemberViewActive ? {} : visibilityMap;
    const lines = computeNotMemberViewMetrics(
      { secondary200, secondary500, primary500, orange500 },
      actualVisibilityMap,
      userDiagnosis
    );

    return { lines };
  }, [
    secondary200,
    secondary500,
    primary500,
    orange500,
    isMemberViewActive,
    userDiagnosis,
    visibilityMap,
  ]);

  const toggleMemberView = useCallback(() => {
    setIsMemberViewActive((currentState) => !currentState);

    if (isMemberViewActive) {
      setVisibilityMap({ [Metrics.ibsScore]: true });
    } else {
      // Restore score and stress metrics visibility
      // when member view has been re-enabled
      setVisibilityMap(visibilityMapInitialState);
    }
  }, [isMemberViewActive]);

  return {
    dataRange,
    isMemberViewActive,
    legendConfig,
    onSelectDataRange: setDataRange,
    onToggleLegendItem,
    overallSymptoms: { value: +over_time_score, trend },
    timeChartConfig,
    timeChartData,
    toggleMemberView,
  };
};
